

export function disableWindowAndWork(callback: TimerHandler) {
    markWorkingPage();

    if (typeof callback === "function") {
        globalThis.setTimeout(callback, 10);
    }
}

let workingPageTimer: ReturnType<typeof globalThis.setTimeout>;

export function markWorkingPage(doNotShowInnerContent?: boolean) {
    let alreadyDisplayed = false;

    if (workingPageTimer) {
        globalThis.clearTimeout(workingPageTimer);
        workingPageTimer = null;
    }

    const wrapper = document.getElementById("PleaseWait");
    if (wrapper) {
        // Initially hide the content and show it after a timeout
        const content = wrapper.querySelector<HTMLElement>(".modalContent");
        content.style.display = "none";

        alreadyDisplayed = wrapper.style.display === "block";

        // Show the wrapper immediately (blocks interaction with the page)
        wrapper.style.display = "block";

        if (!doNotShowInnerContent) {
            workingPageTimer = globalThis.setTimeout(() => {
                content.style.display = "block";
            }, 500);
        }
    }
    return !alreadyDisplayed;
}

export function unmarkWorkingPage(): boolean {
    if (workingPageTimer) {
        globalThis.clearTimeout(workingPageTimer);
        workingPageTimer = null;
    }
    const wrapper = document.getElementById("PleaseWait");
    const wasShown = wrapper.style.display !== "none";
    if (wrapper) {
        wrapper.style.display = "none";
    }
    return wasShown;
}

// Pseudo-export so other windows can call it (needed once converted to modules)
// FUTURE: Remove this once "other windows" stop reaching in to call it
//  (if they even still are any more)
declare global {
    // noinspection JSUnusedGlobalSymbols
    interface Window {
        unmarkWorkingPage: () => boolean;
    }
}
if (globalThis) {
    globalThis.window.unmarkWorkingPage = unmarkWorkingPage;
}

export function wrapWithBusyIndicator(funcReturningPromise: () => Promise<unknown>) {
    if (typeof funcReturningPromise !== "function") {
        throw new TypeError("Bad parameter: funcReturningPromise should be a function");
    }

    // Only "remove" the indicator if we showed it (to allow nesting calls to
    // this function and removing it on the outer call).
    const didShow = markWorkingPage();
    const cleanUpFunc = didShow ? unmarkWorkingPage : () => {};

    const p = funcReturningPromise();
    // Special handling as jQuery deferred/promise do not have finally(),
    // and standard promises do not have always().
    if (typeof p?.finally === "function") {
        return p.finally(cleanUpFunc);
    // @ts-expect-error We are explicitly checking for `always()` so we can handle jQuery Deferred objects
    } else if (typeof p?.always === "function") {
        // @ts-expect-error We are explicitly checking for `always()` so we can handle jQuery Deferred objects
        return p.always(cleanUpFunc);
    } else if (typeof p?.then === "function") {
        // Note that this is not quite equivalent to finally() or always(), but
        // is a "fall-back" for an object that is only "then-able"
        //
        // see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/finally#description
        return p.then(
            (value) => Promise.resolve(() => cleanUpFunc()).then(() => value),
            (error) => Promise.resolve(() => cleanUpFunc()).then(() => { throw error; }),
        );
    } else {
        // No valid promise object returned -- warn the developer but try to
        // "do the right thing" anyway (which is remove the busy indicator and
        // return the result).
        console.warn("`funcReturningPromise` is supposed to return a Promise; %s", funcReturningPromise);
        cleanUpFunc();
        return p;
    }
}
